import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiEdit2, FiCopy, FiTrash2, FiArrowLeft } from 'react-icons/fi';
import templateService from '../../services/templateService';

const TemplateView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTemplate();
  }, [id]);

  const loadTemplate = async () => {
    try {
      const data = await templateService.getTemplateById(id);
      setTemplate(data.template);
    } catch (error) {
      console.error('Error loading template:', error);
      toast.error('Failed to load template');
      navigate('/app/templates');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      try {
        await templateService.deleteTemplate(id);
        toast.success('Template deleted successfully');
        navigate('/app/templates');
      } catch (error) {
        console.error('Error deleting template:', error);
        toast.error(error.response?.data?.error || 'Failed to delete template');
      }
    }
  };

  const handleDuplicate = async () => {
    try {
      await templateService.duplicateTemplate(id);
      toast.success('Template duplicated successfully');
      navigate('/app/templates');
    } catch (error) {
      console.error('Error duplicating template:', error);
      toast.error(error.response?.data?.error || 'Failed to duplicate template');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading template...</div>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Template not found</div>
      </div>
    );
  }

  const getTypeColor = (type) => {
    const colors = {
      'Project': 'bg-blue-100 text-blue-800',
      'Task': 'bg-green-100 text-green-800',
      'Document': 'bg-purple-100 text-purple-800',
      'Report': 'bg-yellow-100 text-yellow-800',
      'Risk': 'bg-red-100 text-red-800',
      'Issue': 'bg-orange-100 text-orange-800',
      'Meeting': 'bg-indigo-100 text-indigo-800',
      'Other': 'bg-gray-100 text-gray-800'
    };
    return colors[type] || colors['Other'];
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/app/templates"
          className="inline-flex items-center text-blue-600 hover:text-blue-800 mb-4"
        >
          <FiArrowLeft className="mr-2" />
          Back to Templates
        </Link>
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{template.name}</h1>
            <p className="text-gray-600 mt-1">{template.description}</p>
          </div>
          <div className="flex space-x-2">
            <Link
              to={`/app/templates/${id}/edit`}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <FiEdit2 className="mr-2" />
              Edit
            </Link>
            <button
              onClick={handleDuplicate}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <FiCopy className="mr-2" />
              Duplicate
            </button>
            <button
              onClick={handleDelete}
              className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              <FiTrash2 className="mr-2" />
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* Template Details */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-4">Basic Information</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">Type</label>
                <span className={`mt-1 inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getTypeColor(template.template_type)}`}>
                  {template.template_type}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Category</label>
                <p className="mt-1 text-gray-900">{template.category}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Status</label>
                <span className={`mt-1 inline-flex px-3 py-1 text-sm font-semibold rounded-full ${template.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                  {template.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Visibility</label>
                <p className="mt-1 text-gray-900">{template.is_public ? 'Public' : 'Private'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Usage Count</label>
                <p className="mt-1 text-gray-900">{template.usage_count || 0}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Created By</label>
                <p className="mt-1 text-gray-900">
                  {template.creator ?
                    `${template.creator.first_name} ${template.creator.last_name}` :
                    'Unknown'
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Template Content */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-4">Template Content</h2>
            <pre className="bg-gray-50 p-4 rounded-md overflow-x-auto text-sm">
              {JSON.stringify(template.content, null, 2)}
            </pre>
          </div>

          {/* Custom Fields */}
          {template.fields && template.fields.length > 0 && (
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-xl font-semibold mb-4">Custom Fields</h2>
              <div className="space-y-3">
                {Array.isArray(template.fields)
                  ? template.fields.map((field, index) => (
                    <div key={index} className="p-3 bg-gray-50 rounded-md">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-medium">{field.name}</span>
                          <span className="text-sm text-gray-500 ml-2">({field.type})</span>
                          {field.required && (
                            <span className="text-xs text-red-600 ml-2">Required</span>
                          )}
                        </div>
                        {field.default_value && (
                          <span className="text-sm text-gray-600">
                            Default: {field.default_value}
                          </span>
                        )}
                      </div>
                    </div>
                  )): null}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Tags */}
          {template.tags && template.tags.length > 0 && (
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-xl font-semibold mb-4">Tags</h2>
              <div className="flex flex-wrap gap-2">
                {Array.isArray(template.fields)
                  ? template.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                  >
                    {tag}
                  </span>
                )): null}
              </div>
            </div>
          )}

          {/* Metadata */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-4">Metadata</h2>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-500">Created</label>
                <p className="mt-1 text-gray-900">
                  {new Date(template.created_at).toLocaleDateString()}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Last Updated</label>
                <p className="mt-1 text-gray-900">
                  {new Date(template.updated_at).toLocaleDateString()}
                </p>
              </div>
              {template.organization && (
                <div>
                  <label className="block text-sm font-medium text-gray-500">Organization</label>
                  <p className="mt-1 text-gray-900">{template.organization.name}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateView;

